import api from "@/api";
import Logger from "@/logger";
import { NoCurrentClassification } from "../exceptions";

const logger = new Logger("store/classifier/actions");

/*************************/
/*         UTILS         */
/*************************/

/**
 * Show a snackbar as a notification indicating an error.
 * @param {Function} commit to commit a mutation
 * @param {String} message the message to show
 */
function notifyError(commit, message) {
  commit("notifications/notify", { type: "error", message }, { root: true });
}

/**
 * Show a snackbar as a notification indicating any successful message.
 * @param {Function} commit to commit a mutation
 * @param {String} message the message to show
 */
function notifySuccess(commit, message) {
  commit("notifications/notify", { type: "success", message }, { root: true });
}

/*************************/
/*        ACTIONS        */
/*************************/

/**
 * Reset the items in the store and get them again by applying the filters.
 * @param {Object} context
 */
function applyFiltersAndGetItems({ commit, dispatch }) {
  commit("setItems", []);
  dispatch("getItems");
}

/**
 * Take all the items with the `correct` property as `true` and remove them of the current classification
 * and move them to the given one.
 * This process saves the predictions of the items for the current classification as incorrect and as correct
 * the one for the other classification (if the items have predictions).
 * @param {Object} context
 * @param {Number} classificationId the id of the classification where the items must belong
 */
async function changeClassificationOfSelectedItems(
  { commit, dispatch, getters, state },
  classificationId
) {
  /* Check if we have the current classification */
  const currentClassification = state.currentClassification;
  if (!currentClassification) throw new NoCurrentClassification();
  const items = getters.itemsWithCorrectProperty.filter((item) => item.correct);

  commit("setLoadingSaveCorrection", true);

  try {
    /* Remove the items from the current classification */
    await api.predictions.classifications.save({
      classificationId: currentClassification.id,
      items: items.map((item) => ({ ...item, correct: false })),
    });

    /* Classify the items in the new classification */
    await api.predictions.classifications.save({ classificationId, items });

    /* Notify the success and get the items again to refresh the content */
    notifySuccess(commit, "Correcciones guardados con éxito");
    commit("setItems", []);
    dispatch("getItems");
  } catch (err) {
    logger.error(err);
    notifyError(commit, "No pudimos mover los QMIs a la otra clasificación");
  }

  commit("setLoadingSaveCorrection", false);
}

async function changeQuantityOfSelectedItems(
  { commit, dispatch, getters },
  quantity
) {
  const items = getters.itemsWithCorrectProperty.filter((item) => item.correct);

  commit("setLoadingSaveCorrection", true);

  try {
    /* Classify the items in the new classification */
    await api.predictions.classifications.updateQuantity({ items, quantity });

    /* Notify the success and get the items again to refresh the content */
    notifySuccess(commit, "Correcciones guardados con éxito");
    commit("setItems", []);
    dispatch("getItems");
  } catch (err) {
    logger.error(err);
    notifyError(commit, "No pudimos mover los QMIs a la otra clasificación");
  }

  commit("setLoadingSaveCorrection", false);
}

/**
 * Create a classification with the given name and get again the classifications.
 *
 * @param {Object} context
 * @param {String} name
 *
 * @returns {Object} with the new classification created
 */
async function createClassification({ commit, dispatch, state }, name) {
  commit("setLoadingCreateClassification", true);
  return api.classifier.classifications
    .create({
      name: name
        .toUpperCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, ""),
      ownerSellerId: state.classificationsSellerId,
    })
    .then(async (classification) => {
      commit("setLoadingCreateClassification", false);
      await dispatch("getClassifications");
      return classification;
    })
    .catch((err) => {
      commit("setLoadingCreateClassification", false);
      throw err;
    });
}

/**
 * Get the classifications using the API and set them in the module's state.
 * @param {Object} context
 */
function getClassifications({ commit, state }) {
  /* Avoid getting the classifications again */
  if (state.loadingClassifications) return;

  commit("loadingClassifications", true);
  return api.classifier.classifications
    .getAll({
      categoriesUrls: state.appSettings.sellersCategories.map(({ url }) => url),
      minLastPriceUpdate: state.appSettings.minLastPriceUpdate,
      ownerSellerId: state.classificationsSellerId,
      sellersIds: state.appSettings.sellersIds,
    })
    .then((classifications) => {
      commit("classifications", classifications);
      commit("loadingClassifications", false);
    })
    .catch((err) => {
      logger.error(err);
      throw err;
    });
}

/**
 * Get the query object to get the items using the correspondent options and filters.
 *
 * THIS IS NOT AN ACTION, is a utility function for other actions.
 *
 * @param {Object} state the current state
 */
function getQueryToGetItems(state) {
  const classification = state.currentClassification;
  if (!classification) throw new NoCurrentClassification();

  return {
    categoriesUrls: state.appSettings.sellersCategories.map(({ url }) => url),
    classificationId: classification.id,
    fetch: state.itemsFetch,
    likePatternBrand: state.likePatternBrandFilter,
    likePatternCategory: state.likePatternCategoryFilter,
    likePatternName: state.likePatternNameFilter,
    minLastPriceUpdate: state.appSettings.minLastPriceUpdate,
    notLikePattern: state.notLikePatternFilter,
    offset: state.items.length,
    onlyBestPrediction: state.onlyItemsWithTheirBestPrediction,
    onlyCorrectPredictions: state.showOnlyCorrectPredictions,
    ownerSellerId: state.classificationsSellerId,
    sellersIds: state.appSettings.sellersIds,
    validated: state.getValidatedItems,
    withoutPredictions: state.showItemsWithoutPredictions,
  };
}

/**
 * Get the items or load more items to complement the current ones of the current classification.
 * @param {Object} context
 */
function getItems({ commit, dispatch, state }) {
  /* Avoid getting the items multiple times */
  if (state.loadingItems) return;

  commit("setLoadingItems", true);
  return api.classifier.items
    .get(getQueryToGetItems(state))
    .then(({ items }) => {
      commit("setItems", [...state.items, ...items]);
      commit(
        "setGetItemsResultMessage",
        items.length
          ? null
          : "No hay QMIs con predicciones o que coincidan con los filtros"
      );
      /* Get the total amount of items for the pagination */
      dispatch("getItemsTotal");
    })
    .catch((err) => {
      logger.error(err);
      notifyError(commit, "No pudimos cargar los items de la clasificación");
      commit("setGetItemsResultMessage", "No pudimos obtener los QMIs");
    })
    .then(() => commit("setLoadingItems", false));
}

/**
 * Get the total number of items to load using the current filters and options of the app.
 *
 * Useful to know how many items the app can load and manage the pagination.
 *
 * @param {Object} context
 */
function getItemsTotal({ commit, state }) {
  commit("setLoadingItemsTotal", true);
  return api.classifier.items.get
    .count(getQueryToGetItems(state))
    .then((total) => commit("setItemsTotal", total))
    .catch((err) => {
      logger.error(err);
      notifyError(commit, "No pudimos cargar el número total de items");
    })
    .then(() => commit("setLoadingItemsTotal", false));
}

async function getClassificationRanking({ commit }) {
  commit("loadingClassRanking", true);
  return api.classifications
    .getClassificationRanking()
    .then((ranking) => {
      commit("classRanking", ranking);
      commit("loadingClassRanking", false);
    })
    .catch((error) => {
      commit("loadingClassRanking", false);
      throw error;
    });
}

async function getSamples({ commit, state }) {
  commit("loadingSamples", true);
  const id = state.currentClassification.id;
  const value = state.termSample;
  return api.classifications
    .getSamples(id, value)
    .then((samples) => {
      commit("samples", samples);
      commit("loadingSamples", false);
    })
    .catch((error) => {
      commit("loadingSamples", false);
      throw error;
    });
}

/**
 * Merge some classifications and keep a single one, where all the items from the other
 * classifications are moved to this one.
 *
 * @param {Object} context
 * @param {Object} idToKeep with the classification's id to keep and merge the other ones into this
 */
function mergeClassifications({ commit, dispatch, state }, idToKeep) {
  const selectedIds = state.selectedClassificationsForMerge.map(({ id }) => id);

  if (!selectedIds || !selectedIds.length) return;

  /* Check the the classification to keep is in the selected ones */
  if (!selectedIds.some((id) => id === idToKeep)) {
    throw new Error(
      "The classificationToKeep is not in the selectedClassificationsForMerge array"
    );
  }

  /* Merge the classifications */
  commit("setLoadingMergeClassifications", true);
  return api.classifier.classifications
    .merge({
      idToKeep,
      othersIds: selectedIds.filter((id) => id !== idToKeep),
    })
    .then(() => dispatch("getClassifications"))
    .catch(() => notifyError(commit, "No pudimos unir las clasificaciones"))
    .then(() => {
      commit("setLoadingMergeClassifications", false);
      commit("setSelectedClassificationsForMerge", []);
    });
}

/**
 * Get again the classifications and the items if there is any `currentClassification`.
 * Very useful when the app settings change and you want to reload the data to reflect
 * the changes in the filters.
 *
 * @param {Object} context
 */
function reloadAppData({ dispatch, state }) {
  dispatch("getClassifications");

  if (state.currentClassification) {
    dispatch("applyFiltersAndGetItems");
  }
}

/**
 * Save the correction of the predictions by taking all the items and sending all of them that have
 * `correct === true` or `correct === false` to the backend.
 * After the saving it will reset the items and get them again to remove the validated ones.
 *
 * @param {Number} [classificationId] if you not provide the classification's id it will use the
 * current one in the store.
 * @param {Object} context
 */
async function saveCorrection(
  { commit, dispatch, getters, state },
  classificationId
) {
  /* Check if we have a classification selected to store its predictions */
  if (!classificationId) {
    const classification = state.currentClassification;
    if (!classification) throw new NoCurrentClassification();
    classificationId = classification.id;
  }

  /* Generate the items to send and raise an error if there are no item to send */
  const items = getters.itemsWithCorrectProperty.map((item) => ({
    id: item.id,
    sellerId: item.sellerId,
    correct: item.correct,
  }));
  if (!items.length) throw new Error("There are no items to send");

  /* Perform the api call */
  commit("setLoadingSaveCorrection", true);
  return api.predictions.classifications
    .save({ classificationId, items })
    .then(() => {
      notifySuccess(commit, "Correcciones guardados con éxito");
      commit("setItems", []);
      dispatch("getItems");
    })
    .catch((err) => {
      logger.error(err);
      notifyError(commit, "Lo sentimos, no pudimos guardar las correcciones");
    })
    .then(() => commit("setLoadingSaveCorrection", false));
}

/**
 * Set the `minLastPriceUpdate` app setting and get the data of the app again.
 *
 * @param {Object} context
 * @param {String} date
 */
function setAppSettingsMinLastPriceUpdate({ commit, dispatch }, date) {
  commit("setAppSettingsMinLastPriceUpdate", date);
  dispatch("reloadAppData");
}

/**
 * Set the selected categories of the selected sellers in the app's settings.
 *
 * @param {Object} context
 * @param {Object[]} categories
 */
function setAppSettingsSellersCategories({ commit, dispatch }, categories) {
  commit("setAppSettingsSellersCategories", categories);
  dispatch("reloadAppData");
}

/**
 * Set the `sellersIds` app setting and get the data of the app again.
 *
 * @param {Object} context
 * @param {Number[]} ids
 */
function setAppSettingsSellersIds({ commit, dispatch }, ids) {
  commit("setAppSettingsSellersIds", ids);
  dispatch("reloadAppData");
}

/**
 * Set the seller id owner of the classifications to work on.
 *
 * This will clean up the app loaded stats if the seller is different from the past one.
 *
 * @param {Object} context
 * @param {Number} id of the seller to set
 */
async function setClassificationsSellerId({ commit, dispatch, state }, id) {
  const availableIds = state.availableClassificationsSellers.map(
    (seller) => seller.id
  );
  if (id && !availableIds.includes(Number(id))) {
    throw new Error("Please provide an available seller id");
  }

  const pastId = state.classificationsSellerId;
  commit("setClassificationsSellerId", id);
  if (pastId !== Number(id)) {
    commit("classifications", []);
    dispatch("setCurrentClassification");
    if (id) await dispatch("getClassifications");
  }
}

/**
 * Set the current classification and reset the categories of it if the id has changed.
 * @param {Object} context
 * @param {Number} id of the classification to set as current.
 */
function setCurrentClassification({ commit, state }, id) {
  const pastId = state.currentClassification
    ? state.currentClassification.id
    : null;
  commit("setCurrentClassification", id);
  if (id && pastId != id) {
    commit("setItems", []);
  }
}

export default {
  applyFiltersAndGetItems,
  changeClassificationOfSelectedItems,
  changeQuantityOfSelectedItems,
  createClassification,
  getClassifications,
  getItems,
  getItemsTotal,
  getSamples,
  getClassificationRanking,
  mergeClassifications,
  reloadAppData,
  saveCorrection,
  setAppSettingsMinLastPriceUpdate,
  setAppSettingsSellersCategories,
  setAppSettingsSellersIds,
  setClassificationsSellerId,
  setCurrentClassification,
};
